"use client";

import { Fade } from "@ecco/ui/fade";
import { Box } from "@ecco/ui/box";
import type { FlexProps } from "@ecco/ui/flex";
import { Flex } from "@ecco/ui/flex";
import { NextImage } from "@ecco/ui/next-image";
import dynamic from "next/dynamic";
import type { FC } from "react";
import { useCallback, useMemo, useState } from "react";
import { space, typography } from "@ecco/ui/tokens";
import { RichText } from "../rich-text-renderer/RichText";
import { styles, getOverlayPlayerStyle } from "./HeroStyle";
import { MotionTitle } from "./hero-content/MotionTitle";
import HeroVideoControls from "./hero-content/HeroVideoControls";
import { HeroCallToActions } from "./hero-content/HeroCallToActions";
import { HeroOutside } from "./hero-outside/HeroOutside";
import type { HeroProps } from "./Hero.types";
import { useHeroModule } from "./useHeroModule";
import { useScrollDownButton } from "./useScrollDownButton";
import { ECCOIcon } from "@ecco/ui/ecco-icon";
import { useHeroVideoMute, useHeroVideoTabOrder, getHeroMediaToggleProps } from "./heroVideo";
import { CommonCMS } from "@lib/constants/contentful";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";

export type { HeroProps } from "./Hero.types";

const HeroVideoPlayer = dynamic(
    () => import("./HeroVideoPlayer").then((mod) => mod.HeroVideoPlayer),
    { ssr: false }
);

export const Hero: FC<HeroProps> = (props) => {
    if (props.contentDisplay === "outside") {
        return <HeroOutside {...props} />;
    }

    return <HeroOverlay {...props} />;
};

const headingDirectionMap = {
    left: ["column", "column", "column", "row-reverse"],
    right: ["column", "column", "column", "row"],
} satisfies Record<string, FlexProps["flexDirection"]>;

const HeroOverlay: FC<HeroProps> = (props) => {
    const {
        id,
        slideIndex = 0,
        height = ["113", "100vh", "100vh", "100vh", "80vh"],
        hasNavigation,
        isCarousel = false,
        headingPosition = "left",
        callToActions,
        description,
        title,
        legalText,
        loop = false,
        videoControls = true,
    } = props;

    const {
        promotionImage,
        shouldRenderVideo,
        shouldRenderImage,
        hasDesktopImage,
        hasMobileImage,
        desktopImageUrl,
        desktopImageAlt,
        mobileImageUrl,
        mobileImageAlt,
        shouldRenderContent,
        shouldShowControls,
        currentVideoId,
        isVideoMuted,
        setIsVideoMuted,
        videoPlayerMuted,
        shouldPlayVideo,
        isMobile,
        isAboveTheFold,
        videoRef,
        promotionRef,
        setVideoPlaying,
        toggleVideoPlayback,
        onPlayerPlayingChange,
        onPlayerEnded,
        isPlaybackConfirmed,
        isPosterVisible,
        shouldMountVideo,
        posterUrl,
    } = useHeroModule({
        ...props,
        respectReducedMotion: false,
    });

    const [animateCompleted, setAnimateCompleted] = useState(!title);
    const { atTop, handleScrollDown, prefersReducedMotion, isEccoKollektivePage } =
        useScrollDownButton();

    const handleAnimationComplete = useCallback(() => {
        setAnimateCompleted(true);
    }, []);

    const videoPlayerId = `hero-video-${id}`;
    const overlayPlayerStyle = useMemo(() => getOverlayPlayerStyle(isMobile), [isMobile]);
    const { toggleMute } = useHeroVideoMute(isVideoMuted, setIsVideoMuted, videoPlayerId);
    const { getMultiple } = useMicrocopy();
    const videoAria = getMultiple(CommonCMS.shared, [
        CommonCMS.video.ariaPlay,
        CommonCMS.video.ariaPause,
    ]);

    useHeroVideoTabOrder(videoPlayerId, shouldMountVideo && Boolean(currentVideoId));

    return (
        <Flex
            data-testid="Hero"
            w="full"
            mx="auto"
            height={height}
            position="relative"
            flexDirection={headingDirectionMap[headingPosition]}
            ref={promotionRef}
            zIndex={0}
            data-video-ready={isPlaybackConfirmed}
        >
            {shouldRenderVideo && currentVideoId && (
                <Box
                    ref={videoRef}
                    h="full"
                    w="full"
                    position="absolute"
                    inset={0}
                    overflow="hidden"
                    {...getHeroMediaToggleProps({
                        enabled: shouldMountVideo,
                        isPlaying: isPlaybackConfirmed,
                        playLabel: videoAria[CommonCMS.video.ariaPlay],
                        pauseLabel: videoAria[CommonCMS.video.ariaPause],
                        onToggle: toggleVideoPlayback,
                    })}
                >
                    {shouldMountVideo && (
                        <HeroVideoPlayer
                            videoId={currentVideoId}
                            isPlaying={shouldPlayVideo}
                            isMuted={videoPlayerMuted}
                            loop={loop}
                            style={overlayPlayerStyle}
                            id={videoPlayerId}
                            onPlayingChange={onPlayerPlayingChange}
                            onEnded={onPlayerEnded}
                        />
                    )}
                    {posterUrl && (
                        <NextImage
                            loading={isAboveTheFold ? "eager" : "lazy"}
                            priority={isAboveTheFold}
                            alt={desktopImageAlt ?? title}
                            src={posterUrl}
                            sizes={["150vw", "100vw", "100vw", "100vw"]}
                            fill
                            objectFit="cover"
                            aria-hidden={!isPosterVisible}
                            css={{
                                ...styles.videoPoster,
                                opacity: isPosterVisible ? 1 : 0,
                                visibility: isPosterVisible ? "visible" : "hidden",
                                transition: "opacity 300ms ease",
                                pointerEvents: "none",
                            }}
                        />
                    )}
                </Box>
            )}

            {shouldShowControls && shouldMountVideo && (
                <Flex css={styles.videoControlsWrapper} zIndex={4} pointerEvents="none">
                    <Box css={styles.videoControlsStickyContainer} pointerEvents="auto">
                        <HeroVideoControls
                            isVideoMuted={isVideoMuted}
                            toggleVideoMute={toggleMute}
                            isVideoPlaying={isPlaybackConfirmed}
                            setVideoPlaying={setVideoPlaying}
                            isCarousel={isCarousel}
                            videoPlayerId={videoPlayerId}
                        />
                    </Box>
                </Flex>
            )}

            {shouldRenderImage && (
                <>
                    {hasMobileImage && (
                        <NextImage
                            loading={isAboveTheFold ? "eager" : "lazy"}
                            priority={isAboveTheFold}
                            alt={mobileImageAlt ?? desktopImageAlt}
                            src={mobileImageUrl}
                            aspectRatio={["2/3"]}
                            display={{ base: "block", sm: "none" }}
                            sizes={["150vw"]}
                            position="relative"
                            fill
                            objectFit="cover"
                        />
                    )}
                    {hasDesktopImage && (
                        <NextImage
                            priority={isAboveTheFold}
                            loading={isAboveTheFold ? "eager" : "lazy"}
                            alt={desktopImageAlt}
                            src={desktopImageUrl}
                            aspectRatio={["4/5", "12/5", "12/5", "12/5"]}
                            display={{
                                base: hasMobileImage ? "none" : "block",
                                sm: "block",
                            }}
                            sizes={["150vw", "100vw", "100vw", "100vw"]}
                            position="relative"
                            fill
                            objectFit="cover"
                        />
                    )}
                </>
            )}

            {atTop && !isCarousel && isEccoKollektivePage && (
                <Flex
                    as="button"
                    role="group"
                    position="fixed"
                    display={{ base: "none", sm: "flex" }}
                    bottom={0}
                    left={0}
                    w="full"
                    h={28}
                    bg="white"
                    alignItems="center"
                    justifyContent="center"
                    zIndex={4}
                    onClick={handleScrollDown}
                    aria-label="Scroll down"
                    border="none"
                    cursor="pointer"
                >
                    <Box
                        sx={{
                            "@keyframes bounce": {
                                "0%, 100%": { transform: "translateY(0)" },
                                "50%": { transform: "translateY(-8px)" },
                            },
                            animation: prefersReducedMotion
                                ? "none"
                                : "bounce 1s ease-in-out infinite",
                        }}
                        _groupHover={{ animationPlayState: "paused" }}
                    >
                        <ECCOIcon name="chevron-light" size="sm" />
                    </Box>
                </Flex>
            )}

            {shouldRenderContent && (
                <Flex
                    style={{
                        position: "absolute",
                        height: "100%",
                        top: "0",
                        flexDirection: "column",
                        justifyContent: "end",
                        width: "100%",
                        backgroundImage:
                            "linear-gradient(180deg, rgba(0, 0, 0, 0.0) 0%, rgba(0, 0, 0, 0.02) 48.44%, rgba(0, 0, 0, 0.42) 100%)",
                    }}
                    zIndex={3}
                    pointerEvents="none"
                >
                    <Box
                        style={{
                            minHeight: "30%",
                            position: "sticky",
                            bottom: "0",
                        }}
                        zIndex={2}
                        pointerEvents="auto"
                    >
                        <Box
                            p={[3, 6]}
                            mb={[1, 0, 0, 0]}
                            style={{
                                boxSizing: "border-box",
                                height: "100%",
                                display: "flex",
                                backgroundImage:
                                    "linear-gradient(180deg, rgba(0, 0, 0, 0.00) 0%, rgba(0, 0, 0, 0.42) 48.44%, rgba(0, 0, 0, 0.42) 100%)",
                            }}
                        >
                            <Flex
                                flexDirection="column"
                                w={["100%", "100%", "70%"]}
                                justifyContent="flex-end"
                                mb={
                                    hasNavigation || videoControls
                                        ? { base: "14.4", sm: 0 }
                                        : undefined
                                }
                            >
                                {title && (
                                    <MotionTitle
                                        mb={space[2]}
                                        handleAnimationComplete={handleAnimationComplete}
                                        title={title}
                                        isFirstOnPage={isAboveTheFold}
                                    />
                                )}
                                <Fade
                                    in={animateCompleted}
                                    unmountOnExit={false}
                                    transition={{
                                        enter: { duration: 0.5 },
                                        exit: { duration: 0.5 },
                                    }}
                                >
                                    {description && (
                                        <Box
                                            fontWeight="bold"
                                            fontSize={typography.fontSizes.mobileBodyTextLarge}
                                            mb={!legalText ? ["6", "8"] : 2}
                                            color="white"
                                        >
                                            {description}
                                        </Box>
                                    )}
                                    {legalText && (
                                        <Box
                                            marginBottom={space["6"]}
                                            fontSize={typography.fontSizes.mobileBodyB4}
                                            color="white"
                                        >
                                            <RichText data={legalText} />
                                        </Box>
                                    )}
                                    <Flex gap={space[0.5]} wrap="wrap" maxW={[null, "80%"]}>
                                        <HeroCallToActions
                                            callToActions={callToActions}
                                            promotionImage={promotionImage}
                                            title={title}
                                            id={id}
                                            slideIndex={slideIndex}
                                            getButtonProps={(button) => ({
                                                variant: "standard",
                                                colorScheme:
                                                    button.variant === "solid" ? "black" : "white",
                                            })}
                                        />
                                    </Flex>
                                </Fade>
                            </Flex>
                        </Box>
                    </Box>
                </Flex>
            )}
        </Flex>
    );
};
